import { useMemo } from 'react';
import { createDoseListItemView } from '~/domain/projections/factories';
import { DoseListItemView } from '~/domain/projections/views';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';

/**
 * One {@link DoseListItemView} per dose loaded into the patient aggregate.
 *
 * Recomputes on {@link useDomainVersion}, so adding or removing a dose is
 * reflected immediately. The previous `useState` + `useEffect` assembly in
 * `useAvailableDoses` keyed off Recoil atom references and could miss an
 * in-place aggregate mutation entirely, leaving a deleted dose in the list.
 *
 * Resolves through the patient repository rather than the session for the
 * same reason as {@link useDoseDetailView} — see its doc comment.
 */
export function useDoseListView(): DoseListItemView[] {
    const patient = useSelectedPatient();
    const domainVersion = useDomainVersion();

    return useMemo(() => {
        if (!patient) {
            return [];
        }
        return patient.doses.map(createDoseListItemView);
        // domainVersion is intentionally a dependency the body never reads:
        // it is the invalidation trigger for in-place aggregate mutations,
        // which change no value React can otherwise observe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patient, domainVersion]);
}
